import { useCallback, useContext, useMemo } from 'react';

import dayjs from 'dayjs';
import flatMap from 'lodash/flatMap';
import sumBy from 'lodash/sumBy';

import { SECONDS_IN_DAY, SECONDS_IN_HOUR } from '@cast/utils';

import {
  OomKillBucketWorkload,
  OOMKillTotals,
} from 'features/cluster/dashboard/WorkloadEventMetricsSection/types';
import {
  fillerFn,
  mergeOomKillDataPoints,
} from 'features/cluster/dashboard/WorkloadEventMetricsSection/utils';
import { useWorkloadEventsQuery } from 'hooks/queries/cost';
import { attachTimeTicks } from 'utils/chart';
import { fillGaps } from 'utils/metrics';

import { TimeRangeSelection } from '../../../_hooks/useCurrentWorkloadMetrics';
import { useRightsizingTimeRangeSelection } from '../../../_hooks/useRightsizingTimeRangeSelection';
import { WorkloadDetailsContext } from '../../_context';

import { useCluster } from '@cast/shared-cluster-context-data-access';

const MANAGED_BY_WOOP = 'woop';

type UseWorkloadOOMKillsDataArgs = {
  containerName?: string;
  timeRangeSelection?: TimeRangeSelection;
};

export const useWorkloadOOMKillsData = ({
  containerName,
  timeRangeSelection,
}: UseWorkloadOOMKillsDataArgs) => {
  const { workload } = useContext(WorkloadDetailsContext);
  const { cluster } = useCluster();
  const rightsizingTimeRangeSelection = useRightsizingTimeRangeSelection();
  const { getRange, key, timeZone } =
    timeRangeSelection ?? rightsizingTimeRangeSelection;
  const range = getRange();

  const stepSeconds = useMemo(() => {
    const diffInSeconds = dayjs(range.to).diff(dayjs(range.from), 'seconds');
    if (diffInSeconds <= SECONDS_IN_DAY * 10) return 300;
    if (diffInSeconds > SECONDS_IN_DAY * 31) return SECONDS_IN_DAY;
    return SECONDS_IN_HOUR;
  }, [range]);

  const {
    data: series,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useWorkloadEventsQuery({
    clusterId: cluster.id,
    namespace: workload?.namespace,
    workloadType: workload?.kind,
    workloadName: workload?.name,
    startTime: range.from,
    endTime: range.to,
    stepSeconds,
    eventTypes: ['OOMKilled'],
    groupByManagedBy: true,
    key,
    enabled: !!workload,
  });

  const rawItems = useMemo(
    () =>
      flatMap(series?.filter((s) => s.eventType === 'OOMKilled') ?? [], (o) => {
        const isManaged = o.managedBy === MANAGED_BY_WOOP;

        return (
          o.items
            ?.map((item) => {
              const containerItem = item.byContainer?.find(
                (c) => c.container === containerName
              );
              if (!containerItem) return null;
              const eventCount = parseInt(containerItem.eventCount, 10);
              return {
                timestamp: item.timestamp,
                managedCount: isManaged ? eventCount : null,
                unmanagedCount: isManaged ? null : eventCount,
              };
            })
            .filter((v): v is NonNullable<typeof v> => v !== null) ?? []
        );
      }),
    [series, containerName]
  );

  const totals = useMemo((): OOMKillTotals => {
    const managed = sumBy(rawItems, 'managedCount');
    const unmanaged = sumBy(rawItems, 'unmanagedCount');
    return { managed, unmanaged, total: managed + unmanaged };
  }, [rawItems]);

  const chartData = useMemo(() => {
    const mergedItems = mergeOomKillDataPoints(rawItems);
    if (mergedItems.length === 0) return [];
    const filled = fillGaps({
      timeSeries: mergedItems,
      dateRange: range,
      filler: fillerFn,
      unit: 'second',
      step: stepSeconds,
    });
    return attachTimeTicks(filled);
  }, [rawItems, range, stepSeconds]);

  const getOomKillBucketPreview = useCallback(
    ({
      bucketTimestamp,
      managed,
    }: {
      bucketTimestamp: string;
      managed: boolean;
    }): OomKillBucketWorkload[] => {
      if (!workload) {
        return [];
      }

      return flatMap(
        series?.filter((s) => {
          if (s.eventType !== 'OOMKilled') {
            return false;
          }
          const isManaged = s.managedBy === MANAGED_BY_WOOP;
          return managed ? isManaged : !isManaged;
        }) ?? [],
        (o) => {
          const item = o.items?.find((i) => i.timestamp === bucketTimestamp);
          if (!item) {
            return [];
          }

          return (item.byContainer ?? [])
            .filter((c) => !containerName || c.container === containerName)
            .map(
              (c): OomKillBucketWorkload => ({
                workloadName: workload.name,
                workloadType: workload.kind,
                namespace: workload.namespace,
                container: c.container,
                eventCount: c.eventCount,
                timestamp: item.timestamp,
              })
            );
        }
      );
    },
    [series, workload, containerName]
  );

  const isOnlyManaged = totals.total !== 0 && totals.managed === totals.total;

  const isOnlyUnmanaged =
    totals.total !== 0 && totals.unmanaged === totals.total;

  return {
    isOnlyManaged,
    isOnlyUnmanaged,
    hasBothEvents: totals.total !== 0 && !isOnlyManaged && !isOnlyUnmanaged,
    chartData,
    isLoading,
    isFetching,
    isError,
    refetch,
    timeZone,
    totals,
    getOomKillBucketPreview,
  };
};
